import { Modal } from '@/components/ui/Modal'
import { PracticeAddressManager } from '@/components/forms/PracticeAddressManager'
import {
  adminCreateProviderAddress,
  adminDeleteProviderAddress,
  adminListProviderAddresses,
  adminUpdateProviderAddress,
} from '@/api/admin'
import type { ProviderResponse } from '@/api/types'

/* ==========================================================================
   Provider Addresses Modal (admin side — issues #140 / #180)

   Wraps the shared PracticeAddressManager with the pharmacy-admin API so a
   pharmacy can manage a provider's practice addresses directly. Used when
   the pharmacy toggles "Manage Provider Addresses" on, and also always
   available as a per-provider action for admins.
   ========================================================================== */

export interface ProviderAddressesModalProps {
  open: boolean
  onClose: () => void
  slug: string
  provider: ProviderResponse | null
  /** Whether to expose the country field on the address editor (#190). */
  showCountryField?: boolean
  /** Pharmacy country used as the default for new addresses (PORTAL-161). */
  defaultCountry?: string
}

export function ProviderAddressesModal({
  open,
  onClose,
  slug,
  provider,
  showCountryField = false,
  defaultCountry,
}: ProviderAddressesModalProps) {
  const title = provider
    ? `Practice Addresses — ${provider.name || provider.email}`
    : 'Practice Addresses'

  return (
    <Modal open={open} onClose={onClose} title={title} size="lg">
      {provider ? (
        <PracticeAddressManager
          showDefaults
          showCountryField={showCountryField}
          defaultCountry={defaultCountry}
          handlers={{
            list: () => adminListProviderAddresses(slug, provider.id),
            create: (data) =>
              adminCreateProviderAddress(slug, provider.id, data),
            update: (addressId, data) =>
              adminUpdateProviderAddress(slug, provider.id, addressId, data),
            remove: (addressId) =>
              adminDeleteProviderAddress(slug, provider.id, addressId),
          }}
        />
      ) : null}
    </Modal>
  )
}

export default ProviderAddressesModal
